/**
 * Static base-elevation field for the particle-lab heightfield.
 *
 * Each texel stores the top Y of the highest AABB collider covering that XZ,
 * or 0 for the floor. Deposited height (heightfield R) accumulates ON TOP of
 * this base; the rendered surface is base + deposit.
 *
 * Stored as a second RGBA32F DataTexture (R = base Y, GBA unused) — not packed
 * into the ping-pong height target's A channel — so reset / splat / compose
 * can keep clearing and adding RGB without restoring a static field every pass.
 */

import * as THREE from 'three';
import type { ArenaXZ, BoxCollider } from './types';

/** World-space Y of the top face of an AABB collider. */
export function colliderTopY(box: BoxCollider): number {
  return box.center[1] + box.half[1];
}

/**
 * True when the XZ point is inside the collider's vertical prism
 * (the footprint that a box-top pile would sit on).
 */
export function colliderCoversXZ(box: BoxCollider, x: number, z: number): boolean {
  return (
    Math.abs(x - box.center[0]) <= box.half[0]
    && Math.abs(z - box.center[2]) <= box.half[2]
  );
}

/** World XZ of the centre of heightfield texel (i, j). */
export function texelWorldXZ(arena: ArenaXZ, size: number, i: number, j: number): [number, number] {
  const u = (i + 0.5) / size;
  const v = (j + 0.5) / size;
  return [arena.minX + u * arena.sizeX, arena.minZ + v * arena.sizeZ];
}

/**
 * CPU bake: `size * size` floats, row-major in the same UV convention as the
 * GPU heightfield (row 0 = minZ, column 0 = minX).
 */
export function bakeBaseElevation(
  size: number,
  arena: ArenaXZ,
  colliders: readonly BoxCollider[],
): Float32Array {
  const out = new Float32Array(size * size);
  if (size <= 0) return out;
  for (let j = 0; j < size; j += 1) {
    for (let i = 0; i < size; i += 1) {
      const [x, z] = texelWorldXZ(arena, size, i, j);
      let top = 0;
      for (const box of colliders) {
        if (!colliderCoversXZ(box, x, z)) continue;
        const y = colliderTopY(box);
        if (y > top) top = y;
      }
      out[j * size + i] = top;
    }
  }
  return out;
}

/** Sample the CPU bake at a world XZ (nearest texel). */
export function sampleBaseElevation(
  data: Float32Array,
  size: number,
  arena: ArenaXZ,
  x: number,
  z: number,
): number {
  if (size <= 0 || data.length < size * size) return 0;
  const u = (x - arena.minX) / arena.sizeX;
  const v = (z - arena.minZ) / arena.sizeZ;
  if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
  const i = Math.min(size - 1, Math.max(0, Math.floor(u * size)));
  const j = Math.min(size - 1, Math.max(0, Math.floor(v * size)));
  return data[j * size + i];
}

export function makeBaseElevationTexture(data: Float32Array, size: number): THREE.DataTexture {
  const rgba = new Float32Array(size * size * 4);
  for (let i = 0; i < size * size; i += 1) rgba[i * 4] = data[i];
  const tex = new THREE.DataTexture(rgba, size, size, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.name = 'particle-lab-base-elevation';
  return tex;
}
